import React from "react";
import styled, { useTheme } from "styled-components";
import PropTypes from "prop-types";
import Button from "components/Button";
import LegacyIcon from "components/icons/legacy-icon";
import Link from "next/link";

const OuterWrapper = styled.section`
  align-items: center;
  background-color: ${({ backgroundColor }) => backgroundColor};
  color: ${({ theme, fontColor }) => fontColor || theme.themeColors.white};
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 4rem 1.5rem;
  text-align: center;
  width: 100%;

  ${({ theme }) => theme.mq.lg`
    padding: 5rem 1.5rem 5rem 1.5rem;

    > div {
      align-self: flex-end;
    }

    :last-of-type {
      padding: 5rem 1.5rem 5rem 0;
    }

    :last-of-type > div {
      align-self: flex-start;
    }
  `}

  ${({ theme }) => theme.mq.xl`
    padding: 6rem 1.5rem 7rem 1.5rem;

    :last-of-type {
      padding: 6rem 1.5rem 7rem 1.5rem;
    }
  `}
`;

const InnerWrapper = styled.div`
  max-width: 45rem;
  width: 100%;
`;

const Description = styled.p`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 auto 1.5rem;
`;

const Title = styled.h3`
  font-size: 2.25rem;
  letter-spacing: -0.066875rem;
  margin: 0 auto 1.5rem;
  width: 100%;

  ${({ theme }) => theme.mq.md`
    font-size: 2.875rem;
    max-width: 38.75rem;
  `}

  ${({ theme }) => theme.mq.lg`
    font-size: 3.5rem;
    max-width: 47.5rem;
  `}

  span {
    font-family: ${({ theme }) => theme.fontStacks.serifItalic};
     font-style: italic;
  }

  br {
    display: block;

    ${({ theme }) => theme.mq.md`
      display: none;
    `}

    ${({ theme }) => theme.mq.lg`
      display: block;
    `}

    ${({ theme }) => theme.mq.xxl`
      display: none;
    `}
  }
`;

const IconGridWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  margin: 0 auto 1.5rem;

  ${({ theme }) => theme.mq.md`
    flex-direction: row;
    max-width: 34.25rem;
    width: 100%;
  `}
`;

const Icons = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  margin: 0 0 2rem;
  min-width: 33%;
`;

const IconWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 1.5rem 0;
  min-height: 5rem;
`;

const IconDescription = styled.p`
  align-self: flex-end;
  font-size: 1.25rem;
  margin: 0 auto;
`;

const ButtonWrapper = styled.a`
  display: flex;
  margin: 0 auto;
  width: 100%;
  max-width: 7.75rem;

  ${({ theme }) => theme.mq.md`
    margin: 0 auto;
  `}

  button {
    height: 3.375rem;
  }
`;

function IconGridCta({
  backgroundColor,
  fontColor,
  icons,
  description,
  title,
  buttonColor,
  buttonLink,
  buttonText,
  buttonTextColor,
}) {
  const theme = useTheme();
  return (
    <OuterWrapper
      backgroundColor={theme.themeColors[backgroundColor]}
      fontColor={theme.themeColors[fontColor]}
    >
      <InnerWrapper>
        <Description>{description}</Description>
        <Title>{title}</Title>
        <IconGridWrapper>
          {icons?.map((item) => {
            const {
              iconColor,
              iconDescription,
              iconHeight,
              iconName,
              iconWidth,
            } = item;
            return (
              <Icons key={iconName}>
                <IconWrapper>
                  <LegacyIcon
                    name={iconName}
                    height={iconHeight}
                    width={iconWidth}
                    color={iconColor}
                  />
                </IconWrapper>
                <IconDescription>{iconDescription}</IconDescription>
              </Icons>
            );
          })}
        </IconGridWrapper>
        <Link href={buttonLink} passHref>
          <ButtonWrapper>
            <Button
              textColor={buttonTextColor}
              colorType={buttonColor}
              outlineType="solid"
            >
              {buttonText}
            </Button>
          </ButtonWrapper>
        </Link>
      </InnerWrapper>
    </OuterWrapper>
  );
}

export default IconGridCta;

IconGridCta.propTypes = {
  backgroundColor: PropTypes.string,
  fontColor: PropTypes.string,
  icons: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  description: PropTypes.shape({}),
  title: PropTypes.shape({}),
  buttonColor: PropTypes.string,
  buttonLink: PropTypes.string,
  buttonText: PropTypes.string,
  buttonTextColor: PropTypes.string,
};

IconGridCta.defaultProps = {
  backgroundColor: "primary",
  fontColor: "white",
  description: "How does GoodFriend work?",
  title: {},
  buttonColor: "primary",
  buttonLink: "/search",
  buttonText: "Join GoodFriend",
  buttonTextColor: "primary",
};
